import Immutable from 'immutable';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';

import {
  TEMP_CATEGORY_ID_PREFIX,
  TOP_LEVEL_CATEGORY_ID,
  TOP_LEVEL_CATEGORY_TITLE_BY_ID
} from 'app/constants/AdminBrowseConstants';
import { isValidUrl } from 'app/helpers/admin/browse';

export const addCategory = (categoryConfig, parentCategoryId, newCategory) => {
  const configCopy = cloneDeep(categoryConfig);

  const { categoryId: newCategoryId } = newCategory;

  const { categoryById } = configCopy;
  const parentCategory = categoryById[parentCategoryId];
  if (!parentCategory) {
    throw new Error(`No parent category found with categoryId: ${parentCategoryId}`);
  }

  categoryById[newCategoryId] = newCategory;
  parentCategory.subCategoryIds.push(newCategoryId);

  return configCopy;
};

// TODO: We need to remove all its children from the index
export const deleteCategoryFromConfig = (categoryId, categoryConfig) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryById = configCopy.categoryById;
  const categoryToDelete = categoryById[categoryId];
  if (!categoryToDelete) {
    throw new Error(`${categoryId} not found in config`);
  }

  const parentCategory = Object.values(categoryById).find(category => {
    const { subCategoryIds = [] } = category;

    return subCategoryIds.indexOf(categoryId) > -1;
  });
  if (!parentCategory) {
    throw new Error(`No parent category found for categoryId: ${categoryId}`);
  }

  const { categoryId: parentCategoryId, subCategoryIds = [] } = parentCategory;
  if (!parentCategoryId) {
    throw new Error('Parent category is missing a categoryId');
  }

  const indexOfChildCategoryId = subCategoryIds.indexOf(categoryId);
  const newSubCategoryIds = subCategoryIds
    .slice(0, indexOfChildCategoryId)
    .concat(subCategoryIds.slice(indexOfChildCategoryId + 1));

  categoryById[parentCategoryId].subCategoryIds = newSubCategoryIds;

  delete categoryById[categoryId];

  return configCopy;
};

export const reOrderSubCategoryIds = (parentCategoryId, newSubCategoryIds, categoryConfig) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryById = configCopy.categoryById;
  const parentCategory = categoryById[parentCategoryId];

  parentCategory.subCategoryIds = newSubCategoryIds;

  return configCopy;
};

export const getSubCategories = (categoryId, categoryConfig) => {
  if (!categoryConfig) {
    return [];
  }

  const { categoryById } = categoryConfig;
  const parentCategory = categoryById[categoryId];
  if (!parentCategory) {
    return [];
  }

  return parentCategory.subCategoryIds.map(subCategoryId => categoryById[subCategoryId]);
};

export const hasAdminPermissions = user => {
  if (!user) {
    return false;
  }

  if (!Immutable.Map.isMap(user)) {
    throw new Error('user must be an Immutable.Map');
  }

  return user.get('permissions').indexOf('BROWSE_CATEGORY_MANAGER') > -1;
};

export const isTopLevelCategoryId = topLevelCategoryId => {
  return Object.values(TOP_LEVEL_CATEGORY_ID).includes(topLevelCategoryId);
};

export const isUnsavedCategory = category => {
  return category.categoryId.startsWith(TEMP_CATEGORY_ID_PREFIX);
};

export const makeCategoryConfigId = (subdomain, localeCode, topLevelCategoryId) => {
  const categoryId = topLevelCategoryId.substring(1); // remove initial `/`
  return `${subdomain}:${localeCode}:${categoryId}`;
};

export const makeEmptyCategory = (categoryId = null, { isEnabled = false, title = '' } = {}) => {
  return {
    categoryId: categoryId || uuidv4(),
    isEnabled: isEnabled || false,
    searchUrl: '',
    slug: '',
    subCategoryIds: [],
    title
  };
};

export const makeEmptyCategoryConfig = (subdomain, localeCode, topLevelCategoryId) => {
  const categoryConfigId = null;
  const title = TOP_LEVEL_CATEGORY_TITLE_BY_ID?.[topLevelCategoryId] ?? '';

  return {
    categoryConfigId,
    categoryConfigSchemaVersion: '1.0.0',
    categoryById: {
      [topLevelCategoryId]: makeEmptyCategory(topLevelCategoryId, { isEnabled: true, title })
    },
    localeCode,
    subdomain,
    topLevelCategoryId,
    updatedAt: Date.now(),
    updatedBy: ''
  };
};

export const makeCategorySlug = categoryTitle => {
  // remove accents, symbols and punctuation
  let slug = categoryTitle
    .normalize('NFKD') // decompose accented chars
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .replace(/[^a-zA-Z0-9]+/g, '-') // replace non-alphanumeric with dashes
    .replace(/^-+|-+$/g, '') // trim leading/trailing dashes
    .toLowerCase();

  // if the result is empty, URI-encode it and remove invalid characters
  if (!slug) {
    slug = encodeURIComponent(categoryTitle)
      .replace(/%/g, '')
      .replace(/\+/g, '-') // URL-safe
      .replace(/\//g, '_')
      .replace(/=+$/, '')
      .toLowerCase();
  }

  return slug;
};

export const validateCategory = categoryCandidate => {
  const { searchUrl, title } = categoryCandidate;

  const isValidSearchUrl = isValidUrl(searchUrl);
  const isValidTitle = Boolean(title);

  const isValid = isValidSearchUrl && isValidTitle;

  return {
    isValid,
    errorByAttribute: {
      searchUrl: isValidSearchUrl ? '' : 'A catalog search URL is required',
      title: isValidTitle ? '' : 'A title is required'
    }
  };
};
